import {
  Avatar,
  Box,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Typography,
} from "@mui/material";
import Conexao from "../../../../models/conexao";
import FelixCard from "../../../util/ui/felixCard";
import { useEffect, useState } from "react";
import Corretora from "../../../../models/corretora";
import { getDoc } from "firebase/firestore";
import CorretoraDb from "../../../../db/corretoraDb";
import { SituacaoSincronizacao } from "../../../../models/enum";
import moment from "moment";
import { Link } from "react-router-dom";
import { IconBuildingBank } from "@tabler/icons-react";
import { radius } from "../../../../theme";

type Props = {
  conexao: Conexao;
};

/** Cada situação da conexão cai em UMA das cinco cores semânticas de status
 *  (DESIGN.md → Chips de status). Mesma árvore de decisão de antes. */
type TomStatus = "positive" | "accent" | "warn" | "destructive";

function statusDaConexao(conexao: Conexao): { rotulo: string; tom: TomStatus } {
  switch (conexao.situacao) {
    case SituacaoSincronizacao.SINCRONIZADA:
      if (conexao.dataAtualizacao?.isBefore(moment(), "day")) {
        return { rotulo: "Desatualizada", tom: "warn" };
      }
      return { rotulo: "Conectada", tom: "positive" };
    case SituacaoSincronizacao.ATUALIZANDO:
    case SituacaoSincronizacao.CRIADA:
    case SituacaoSincronizacao.SINCRONIZANDO:
      return { rotulo: "Sincronizando", tom: "accent" };

    case SituacaoSincronizacao.ERRO_AO_LER:
    case SituacaoSincronizacao.ERRO_AO_SINCRONIZAR:
    default:
      return { rotulo: "Erro", tom: "destructive" };
  }
}

export default function CardConexao({ conexao }: Props) {
  const [corretora, setCorretora] = useState<Corretora>();
  useEffect(() => {
    if (!!conexao.corretora) {
      getDoc(new CorretoraDb().doc({ corretoraId: conexao.corretora })).then(
        (doc) => setCorretora(doc.data())
      );
    }
  }, [conexao.corretora]);

  const { rotulo, tom } = statusDaConexao(conexao);

  return (
    <FelixCard>
      <CardActionArea component={Link} to={`/arquivo/conexao/${conexao.id}`}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" pt={2}>
            <Avatar
              variant="rounded"
              sx={(theme) => ({
                width: 64,
                height: 64,
                fontSize: 32,
                borderRadius: `${radius.lg}px`,
                // Fundo neutro quente atrás do logo da instituição — nunca o
                // cinza padrão do MUI, que briga com o papel da marca.
                bgcolor: theme.palette.ink["08"],
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.divider}`,
              })}
              src={corretora?.avatar}
            >
              <IconBuildingBank size={32} />
            </Avatar>
            <Typography variant="h3" align="center" color="text.primary" pt={2}>
              {corretora?.apelido || <Skeleton width={60} />}
            </Typography>
            <Box pt={0.5} pb={1.5}>
              {conexao.conta === "" ? (
                <Skeleton width={100} />
              ) : (
                <Typography variant="dataMono" color="text.secondary">
                  {conexao.conta}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              label={rotulo}
              sx={(theme) => ({
                bgcolor: theme.palette.statusChip[tom].bg,
                color: theme.palette.statusChip[tom].fg,
              })}
            />
            <Divider sx={{ width: "100%", my: 2 }} />
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-around"
              width="100%"
            >
              <Box
                flexBasis={1}
                flexGrow={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={0.5}
              >
                <Typography variant="labelMono" color="text.secondary">
                  Criada em:
                </Typography>
                <Typography
                  variant="dataMono"
                  color="text.primary"
                  fontSize={12}
                >
                  {conexao.dataConexao.format("DD/MM/YYYY HH:mm")}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box
                flexBasis={1}
                flexGrow={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={0.5}
              >
                <Typography variant="labelMono" color="text.secondary">
                  Atualizada em:
                </Typography>
                <Typography
                  variant="dataMono"
                  color="text.primary"
                  fontSize={12}
                >
                  {conexao.dataAtualizacao?.format("DD/MM/YYYY HH:mm")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </FelixCard>
  );
}
